import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import IconPlay from 'components/icons/IconPlay';
import IconPlus from 'components/icons/iconPlus';
import IconMinus from 'components/icons/iconMinus';
import IconClose from 'components/icons/iconClose';
import IconArrowLeftWhite from 'components/icons/Slider/iconArrowLeftWhite';
import IconArrowRightWhite from 'components/icons/Slider/iconArrowRightWhite';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import useVideoMetadata from 'hooks/useVideoMetadata';
import useIsVideoPosterPlayable from 'hooks/useIsVideoPosterPlayable';
import usePinchZoom from 'hooks/usePinchZoom';
import useIsHoverDevice from 'hooks/useIsHoverDevice';
import useTemporaryReveal from 'hooks/useTemporaryReveal';
import useScrollSelectedIntoView from 'hooks/useScrollSelectedIntoView';
import { GALLERY_IMAGE_LOCATION, GALLERY_INTERACTION } from 'data-hooks/useEvents/pdpGalleryEventConstants';
import { getMainImageImgixPropsForPhoto } from '../PDPDesktopHeroGallery/helpers/imgix-props';
import getMobileGallerySizes from './getMobileGallerySizes';
import PDPProductIntroVideo from '../PDPDesktopHeroGallery/PDPProductIntroVideo';
import PDPGalleryRevampCircleButton from '../PDPDesktopGalleryRevamp/PDPGalleryRevampCircleButton';
import PDPGalleryRevampLifestyleThumb from '../PDPDesktopGalleryRevamp/PDPGalleryRevampLifestyleThumb';
const MAX_ZOOM = 3;

// Square letterbox on the product's background, spelled out so it does not depend
// on JBProductImage's no-crop-data fallback, which is 2:1 (CON-8753).
const MOBILE_SLIDE_IMGIX_PROPS = {
  ar: '1:1',
  crop: 'center',
  fill: 'solid',
  fillColor: 'F5F6F8',
  fit: 'fill',
  trim: 'color',
  trimColor: 'F5F6F8',
  trimPad: 180,
  trimTol: 50
};
const THUMB_CLASS = 'h-16 w-20 shrink-0 snap-start';

// How long the scroller must hold still before the photo counts as viewed
const SCROLL_SETTLE_DELAY = 150;

// Expanded lifestyle gallery (CON-8444 mobile): full-bleed photo + bottom thumb strip
const PDPMobileGalleryRevampExpandedGallery = ({
  initialIndex = 0,
  introVideoId,
  onClose,
  photos = [],
  productName,
  trackGalleryInteracted,
  viewGalleryImageEvent
}) => {
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [isVideoSelected, setIsVideoSelected] = useState(false);
  const photoCount = photos.length;
  const hasMultiplePhotos = photoCount > 1;
  const scrollerRef = useRef(null);
  const thumbStripRef = useRef(null);
  // Last index the scroller settled on, so analytics fires once per photo
  const settledIndexRef = useRef(initialIndex);
  // Last index reported, so settling back onto it - or onto the photo the
  // gallery opened at - stays quiet
  const reportedIndexRef = useRef(initialIndex);
  // Photo picked while the video was showing, applied once the scroller is back
  const pendingIndexRef = useRef(null);
  const {
    thumbnail: videoThumbnailUrl,
    videoUrl: videoPosterUrl
  } = useVideoMetadata({
    videoId: introVideoId
  });
  const isPosterPlayable = useIsVideoPosterPlayable(videoPosterUrl);
  const showVideoTile = !!introVideoId && (!!videoThumbnailUrl || isPosterPlayable);

  // The video tile takes the first slot, so a photo's thumb sits one along
  useScrollSelectedIntoView({
    scrollerRef: thumbStripRef,
    selectedIndex: isVideoSelected ? 0 : selectedIndex + (showVideoTile ? 1 : 0)
  });
  const isHoverDevice = useIsHoverDevice();

  // Kept in a ref so usePinchZoom's onTap can stay a stable identity
  const revealArrowsRef = useRef(null);
  const {
    canZoomIn,
    canZoomOut,
    containerRef,
    contentRef,
    isZoomed,
    reset: resetZoom,
    scaleStep,
    zoomIn,
    zoomOut
  } = usePinchZoom({
    maxScale: MAX_ZOOM,
    // Pinch and double-tap zoom count the same as the +/- buttons; the hook
    // fires this once per completed gesture, not per frame
    onGestureZoom: useCallback(() => trackGalleryInteracted?.(GALLERY_INTERACTION.ZOOMED_INTERACTED), [trackGalleryInteracted]),
    onTap: useCallback(() => revealArrowsRef.current?.(), [])
  });

  // A pointer device keeps the arrows up, since there is no tap to re-show them
  const {
    isRevealed: areArrowsRevealed,
    reveal: revealArrows
  } = useTemporaryReveal({
    held: isZoomed && !isHoverDevice
  });
  revealArrowsRef.current = revealArrows;
  const areArrowsVisible = isHoverDevice ? !isZoomed : areArrowsRevealed;
  // Zoom out has to stay reachable while zoomed, even once the arrows go
  const areZoomButtonsVisible = areArrowsVisible || isZoomed;

  // JBImage needs explicit sizes inside the zoomed wrapper
  const mainPhotoSizes = getMobileGallerySizes(scaleStep);
  const basePhotoSizes = getMobileGallerySizes(1);
  const reportPhotoView = useCallback(index => {
    const src = photos[index]?.src;
    viewGalleryImageEvent?.({
      imageLocation: GALLERY_IMAGE_LOCATION.LIFESTYLE,
      imageFilename: src?.split('/').pop() || '',
      position: index,
      totalImages: photos.length
    });
  }, [photos, viewGalleryImageEvent]);

  // Reports a photo at most once per stretch of viewing it
  const reportPhotoViewOnce = useCallback(index => {
    if (index === reportedIndexRef.current) {
      return;
    }
    reportedIndexRef.current = index;
    reportPhotoView(index);
  }, [reportPhotoView]);

  // Scrolling is the source of truth: move the scroller and let its handler
  // update the index, so a thumb tap and a swipe end up in the same place
  const scrollToPhoto = useCallback((index, smooth = true) => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    scroller.scrollTo({
      left: index * scroller.clientWidth,
      behavior: smooth ? 'smooth' : 'auto'
    });
  }, []);
  const selectPhoto = index => {
    setIsVideoSelected(false);
    resetZoom();
    if (isVideoSelected) {
      // Nothing to scroll yet: the scroller is unmounted while the video shows
      pendingIndexRef.current = index;
      return;
    }
    scrollToPhoto(index);
  };

  // Stops at both ends. A snap scroller cannot wrap.
  const goToPrevious = useCallback(() => scrollToPhoto(Math.max(selectedIndex - 1, 0)), [scrollToPhoto, selectedIndex]);
  const goToNext = useCallback(() => scrollToPhoto(Math.min(selectedIndex + 1, photoCount - 1)), [photoCount, scrollToPhoto, selectedIndex]);

  // Jump to the thumbnail that was tapped, before the first paint so it never
  // reads as the gallery scrolling on open
  useLayoutEffect(() => {
    scrollToPhoto(initialIndex, false);
    // Only on mount: afterwards the scroller owns its own position
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The scroller remounts at its old position, so jump before the first paint
  useLayoutEffect(() => {
    if (isVideoSelected || pendingIndexRef.current == null) {
      return;
    }
    const index = pendingIndexRef.current;
    pendingIndexRef.current = null;
    scrollToPhoto(index, false);
    setSelectedIndex(index);
    settledIndexRef.current = index;
    // Reported here because the instant scroll's event may land before the
    // listener re-attaches to the freshly mounted scroller
    reportPhotoViewOnce(index);
  }, [isVideoSelected, scrollToPhoto, reportPhotoViewOnce]);
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return undefined;
    }
    let frame = null;
    let reportTimeout = null;

    // Reads the index at fire time, not closing over it: the timer restarts on
    // scroll activity, since a smooth scroll's final step can outlast the delay
    const reportSettledPhoto = () => {
      reportTimeout = null;
      reportPhotoViewOnce(settledIndexRef.current);
    };
    const handleScroll = () => {
      // The UI follows the scroller live, but analytics waits for it to stop
      clearTimeout(reportTimeout);
      reportTimeout = setTimeout(reportSettledPhoto, SCROLL_SETTLE_DELAY);
      if (frame) {
        return;
      }

      // Coalesced to one read per frame, since scroll fires far more often
      frame = requestAnimationFrame(() => {
        frame = null;
        const {
          clientWidth
        } = scroller;
        if (!clientWidth) {
          return;
        }
        const index = Math.round(scroller.scrollLeft / clientWidth);
        if (index !== settledIndexRef.current) {
          settledIndexRef.current = index;
          setSelectedIndex(index);
          revealArrowsRef.current?.();
        }
      });
    };
    scroller.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      clearTimeout(reportTimeout);
      scroller.removeEventListener('scroll', handleScroll);
    };
    // isVideoSelected: the video unmounts the scroller, so re-bind to the new one
  }, [reportPhotoViewOnce, isVideoSelected]);
  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  return <div className="flex flex-col h-full" data-xc="PDPMobileGalleryRevampExpandedGallery">
      <div className="relative flex-1 min-h-0">
        {isVideoSelected && showVideoTile ? <div className="h-full w-full bg-gray-light8">
            <PDPProductIntroVideo videoId={introVideoId} />
          </div> : <div ref={scrollerRef} className={classnames('h-full flex bg-gray-light8 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
      // Zooming hands every gesture to usePinchZoom, so snapping and
      // horizontal scrolling both have to stop or they fight the pan
      isZoomed ? 'overflow-hidden' : 'overflow-x-auto overflow-y-hidden snap-x snap-mandatory')}>
            {photos.map((photo, index) => {
          const isActive = index === selectedIndex;
          return <div key={`PDPMobileGalleryRevampSlide${photo.id}`}
          // Zoom is mounted on the active slide only, so usePinchZoom's
          // refs follow the photo actually on screen
          ref={isActive ? containerRef : undefined}
          // pan-x keeps snap paging, pan-y keeps the page scrollable
          // behind the photo. Leaving pinch-zoom out of the list is
          // what stops the browser zooming the page.
          style={{
            touchAction: isZoomed ? 'none' : 'pan-x pan-y'
          }}
          // snap-always caps a gesture at one photo, otherwise a hard
          // flick skips several
          className="h-full w-full shrink-0 snap-center snap-always overflow-hidden">
                  <div
            // usePinchZoom writes transform straight to this node. Origin
            // is top-left so the pinch focal point maths stays simple
            ref={isActive ? contentRef : undefined} className={classnames('h-full w-full origin-top-left', {
              'will-change-transform': isActive
            })}>
                    <JBProductImage className="h-full w-full object-contain select-none pointer-events-none" alt={productName ? String(productName) : ''} {...getMainImageImgixPropsForPhoto(photo, false)}
              // Crop data is a small window into a much larger photo, so
              // it reads as wrongly zoomed in here (CON-8753).
              crops={{}} {...MOBILE_SLIDE_IMGIX_PROPS} sizes={isActive ? mainPhotoSizes : basePhotoSizes} lazy={Math.abs(index - initialIndex) > 1} priority={index === initialIndex} />
                  </div>
                </div>;
        })}
          </div>}

        {/* Faded rather than unmounted, so they can come back without popping.
            Hidden while zoomed so they never sit on top of a panning gesture. */}
        {hasMultiplePhotos && !isVideoSelected && <div className={classnames('transition-opacity duration-[400ms] ease-out', areArrowsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none')} aria-hidden={!areArrowsVisible}>
            {selectedIndex > 0 && <div className="absolute left-4 top-1/2 -translate-y-1/2 z-[2]">
                <PDPGalleryRevampCircleButton ariaLabel="Previous image" onClick={goToPrevious}
          // Faded arrows stay in the DOM, so drop them out of the tab order
          tabIndex={areArrowsVisible ? undefined : -1}>
                  <IconArrowLeftWhite width={13} height={13} />
                </PDPGalleryRevampCircleButton>
              </div>}
            {/* left-4 / right-4 to share the close and zoom column's inset */}
            {selectedIndex < photoCount - 1 && <div className="absolute right-4 top-1/2 -translate-y-1/2 z-[2]">
                <PDPGalleryRevampCircleButton ariaLabel="Next image" onClick={goToNext} tabIndex={areArrowsVisible ? undefined : -1}>
                  <IconArrowRightWhite width={13} height={13} />
                </PDPGalleryRevampCircleButton>
              </div>}
          </div>}

        {/* Same spot as the default state's circle-button row */}
        <div className="absolute flex flex-col gap-2.5 right-4 top-4 z-[2]">
          <PDPGalleryRevampCircleButton ariaLabel="Close gallery" onClick={onClose}>
            <IconClose width={9} height={9} />
          </PDPGalleryRevampCircleButton>
          {!isVideoSelected &&
        // Fades with the arrows, but stays up while zoomed so zoom out
        // never disappears mid-zoom
        <div className={classnames('flex flex-col gap-2.5 transition-opacity duration-[400ms] ease-out', areZoomButtonsVisible ? 'opacity-100' : 'opacity-0 pointer-events-none')} aria-hidden={!areZoomButtonsVisible}>
              <PDPGalleryRevampCircleButton ariaLabel="Zoom in" disabled={!canZoomIn} onClick={() => {
            zoomIn();
            trackGalleryInteracted?.(GALLERY_INTERACTION.ZOOMED_INTERACTED);
          }} tabIndex={areZoomButtonsVisible ? undefined : -1}>
                <IconPlus width={15} height={15} />
              </PDPGalleryRevampCircleButton>
              <PDPGalleryRevampCircleButton ariaLabel="Zoom out" disabled={!canZoomOut} onClick={() => {
            zoomOut();
            trackGalleryInteracted?.(GALLERY_INTERACTION.ZOOMED_INTERACTED);
          }} tabIndex={areZoomButtonsVisible ? undefined : -1}>
                <IconMinus width={15} height={15} />
              </PDPGalleryRevampCircleButton>
            </div>}
        </div>
      </div>

      {/* px so the first and last thumb can scroll clear of the edge instead of
          resting half-cut. snap keeps them landing on a whole thumbnail. */}
      <div ref={thumbStripRef} className="flex gap-1.5 overflow-x-auto snap-x px-2 py-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
        {showVideoTile && <PDPGalleryRevampLifestyleThumb ariaLabel="Play product video" className={THUMB_CLASS} imgSize={{
        width: 160,
        height: 128
      }} src={videoThumbnailUrl} videoUrl={videoPosterUrl} isSelected={isVideoSelected} onClick={() => {
        setIsVideoSelected(true);
        resetZoom();
      }} icon={<IconPlay width={20} height={20} />} />}
        {photos.map(({
        id,
        src
      }, index) => <PDPGalleryRevampLifestyleThumb key={`PDPMobileGalleryRevampExpandedThumb${id}`} className={THUMB_CLASS} imgSize={{
        width: 160,
        height: 128
      }} src={src} isSelected={!isVideoSelected && index === selectedIndex} onClick={() => selectPhoto(index)} />)}
      </div>
    </div>;
};
PDPMobileGalleryRevampExpandedGallery.propTypes = {
  initialIndex: PropTypes.number,
  introVideoId: PropTypes.string,
  onClose: PropTypes.func,
  photos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    src: PropTypes.string.isRequired
  })),
  productName: PropTypes.string,
  trackGalleryInteracted: PropTypes.func,
  viewGalleryImageEvent: PropTypes.func
};
export default PDPMobileGalleryRevampExpandedGallery;